import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, CancelTokenSource } from 'axios';
import { config as appConfig } from '../config/config';

// API Error types
export interface ApiError {
    status: number;
    message: string;
    code?: string;
    data?: any;
}

// Request configuration
const apiConfig: AxiosRequestConfig = {
    timeout: 60000,
    headers: {
        'Content-Type': 'application/json'
    }
};

// Retry configuration
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

// Create a custom event for server errors
export const SERVER_ERROR_EVENT = 'server-error';

// Create API client instance
export class ApiClient {
    private client: AxiosInstance;

    constructor(config: AxiosRequestConfig = {}) {
        this.client = axios.create({
            ...apiConfig,
            ...config,
        });

        // Request interceptor
        this.client.interceptors.request.use(
            config => {
                // Get token from localStorage
                const token = localStorage.getItem('auth_token');
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }

                // Set the baseURL
                config.baseURL = appConfig.api.baseUrl;

                return config;
            },
            error => Promise.reject(error)
        );

        // Response interceptor
        this.client.interceptors.response.use(
            response => response,
            error => {
                if (error.response?.status === 401) {
                    // Clear auth data
                    localStorage.removeItem('auth_token');
                    localStorage.removeItem('user');
                    
                    // Only redirect to login page if we're not already on the login page
                    if (!window.location.pathname.includes('/login')) {
                        window.location.href = '/login';
                    }
                } else if (error.response?.status === 500) {
                    // Dispatch custom event for server errors
                    window.dispatchEvent(new CustomEvent(SERVER_ERROR_EVENT, {
                        detail: {
                            message: 'Unexpected error occurred. Please try again after some time.'
                        }
                    }));
                }
                return Promise.reject(error);
            }
        );
    }



    // Cancel all pending requests
    public cancelAllRequests(): void {
        // No-op since we removed the pending requests tracking
    }

    // Check if an error is retryable
    private isRetryableError(error: any): boolean {
        // Network errors (connection issues, timeouts, etc.)
        if (error.code === 'ERR_NETWORK' || 
            error.code === 'ERR_CANCELED' ||
            error.code === 'ECONNABORTED' ||
            error.message?.includes('ConnectionError') ||
            error.message?.includes('Failed to connect') ||
            error.message?.includes('timeout') ||
            error.message?.includes('network') ||
            error.message?.includes('ECONNREFUSED') ||
            error.message?.includes('ENOTFOUND')) {
            return true;
        }

        // Server errors that might be temporary
        if (error.response?.status >= 500 && error.response?.status < 600) {
            return true;
        }

        // Specific Azure SQL connection errors
        if (error.message?.includes('kavachsqlserver.database.windows.net') ||
            error.message?.includes('1433') ||
            error.message?.includes('15000ms')) {
            return true;
        }

        return false;
    }

    // Generic GET method
    public async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        let attempt = 0;
        while (attempt < MAX_RETRIES) {
            try {
                const response: AxiosResponse<T> = await this.client.get(url, config);
                return response.data;
            } catch (error: any) {
                attempt++;
                
                // Check if this is a retryable error
                const isRetryableError = this.isRetryableError(error);
                
                if (isRetryableError && attempt < MAX_RETRIES) {
                    console.warn(`API call failed (attempt ${attempt}/${MAX_RETRIES}):`, error.message);
                    await new Promise(res => setTimeout(res, RETRY_DELAY * attempt)); // Exponential backoff
                } else {
                    throw error;
                }
            }
        }
        throw new Error('Max retries reached');
    }

    // Generic POST method
    public async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        let attempt = 0;
        while (attempt < MAX_RETRIES) {
            try {
                const response: AxiosResponse<T> = await this.client.post(url, data, config);
                return response.data;
            } catch (error: any) {
                if (error?.response?.status === 429) {
                    throw error;
                }
                attempt++;

                // Check if this is a retryable error
                const isRetryableError = this.isRetryableError(error);
                
                if (isRetryableError && attempt < MAX_RETRIES) {
                    console.warn(`API call failed (attempt ${attempt}/${MAX_RETRIES}):`, error.message);
                    await new Promise(res => setTimeout(res, RETRY_DELAY * attempt)); // Exponential backoff
                } else {
                    throw error;
                }
            }
        }
        throw new Error('Max retries reached');
    }

    // Generic PUT method
    public async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        let attempt = 0;
        while (attempt < MAX_RETRIES) {
            try {
                const response: AxiosResponse<T> = await this.client.put(url, data, config);
                return response.data;
            } catch (error: any) {
                attempt++;
                
                // Check if this is a retryable error
                const isRetryableError = this.isRetryableError(error);
                
                if (isRetryableError && attempt < MAX_RETRIES) {
                    console.warn(`API call failed (attempt ${attempt}/${MAX_RETRIES}):`, error.message);
                    await new Promise(res => setTimeout(res, RETRY_DELAY * attempt)); // Exponential backoff
                } else {
                    throw error;
                }
            }
        }
        throw new Error('Max retries reached');
    }

    // Generic PATCH method
    public async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        let attempt = 0;
        while (attempt < MAX_RETRIES) {
            try {
                const response: AxiosResponse<T> = await this.client.patch(url, data, config);
                return response.data;
            } catch (error: any) {
                attempt++;
                
                // Check if this is a retryable error
                const isRetryableError = this.isRetryableError(error);
                
                if (isRetryableError && attempt < MAX_RETRIES) {
                    console.warn(`API call failed (attempt ${attempt}/${MAX_RETRIES}):`, error.message);
                    await new Promise(res => setTimeout(res, RETRY_DELAY * attempt)); // Exponential backoff
                } else {
                    throw error;
                }
            }
        }
        throw new Error('Max retries reached');
    }

    // Generic DELETE method
    public async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        let attempt = 0;
        while (attempt < MAX_RETRIES) {
            try {
                const response: AxiosResponse<T> = await this.client.delete(url, config);
                return response.data;
            } catch (error: any) {
                attempt++;
                
                // Check if this is a retryable error
                const isRetryableError = this.isRetryableError(error);
                
                if (isRetryableError && attempt < MAX_RETRIES) {
                    console.warn(`API call failed (attempt ${attempt}/${MAX_RETRIES}):`, error.message);
                    await new Promise(res => setTimeout(res, RETRY_DELAY * attempt)); // Exponential backoff
                } else {
                    throw error;
                }
            }
        }
        throw new Error('Max retries reached');
    }
}

// Create and export a default instance
const apiClient = new ApiClient();
export default apiClient;
